import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, Trash2, Edit, Check, X, Upload, Save, LogOut, Eye, EyeOff, User, BarChart3, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { usePageTitle } from '../branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  usePageTitle('BuildBroz Admin — Dashboard');
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingGalleryImage, setEditingGalleryImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newImageData, setNewImageData] = useState({
    title: '',
    description: '',
    category: 'Kitchen Remodeling',
    location: 'Cleveland, OH',
    year: new Date().getFullYear().toString()
  });
  
  // Account management
  const [accountForm, setAccountForm] = useState({
    username: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPasswordAccount, setShowNewPasswordAccount] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/ops/login');
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, getAuthHeaders());
      loadData();
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/ops/login');
    }
  };

  const loadData = async () => {
    try {
      const [reviewsRes, galleryRes, settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/reviews`, getAuthHeaders()),
        axios.get(`${API}/admin/gallery`, getAuthHeaders()),
        axios.get(`${API}/admin/settings`, getAuthHeaders()),
        axios.get(`${API}/admin/stats`, getAuthHeaders())
      ]);

      setReviews(reviewsRes.data);
      setGalleryImages(galleryRes.data);
      setSiteSettings(settingsRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/ops/login');
  };

  // Review Management
  const updateReview = async (reviewId, updates) => {
    try {
      await axios.patch(`${API}/admin/reviews/${reviewId}`, updates, getAuthHeaders());
      loadData();
    } catch (error) {
      console.error('Failed to update review:', error);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, getAuthHeaders());
      loadData();
    } catch (error) {
      console.error('Failed to delete review:', error);
    }
  };

  // Gallery Management
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setNewImageFile(file);
    // Reset form data
    setNewImageData({
      title: '',
      description: '',
      category: 'Kitchen Remodeling',
      location: 'Cleveland, OH',
      year: new Date().getFullYear().toString()
    });
  };

  const handleImageUpload = async () => {
    if (!newImageFile || !newImageData.title || !newImageData.description) {
      alert('Please fill in all required fields');
      return;
    }

    setUploadingImage(true);
    try {
      // Upload image
      const formData = new FormData();
      formData.append('file', newImageFile);
      
      const uploadRes = await axios.post(`${API}/admin/gallery/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Create gallery entry
      const params = new URLSearchParams();
      params.append('image_url', uploadRes.data.image_url);
      
      await axios.post(
        `${API}/admin/gallery?${params.toString()}`,
        newImageData,
        getAuthHeaders()
      );

      // Reset and reload
      setNewImageFile(null);
      setNewImagePreview(null);
      setNewImageData({
        title: '',
        description: '',
        category: 'Kitchen Remodeling',
        location: 'Cleveland, OH',
        year: new Date().getFullYear().toString()
      });
      loadData();
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const cancelImageUpload = () => {
    setNewImageFile(null);
    setNewImagePreview(null);
    setNewImageData({
      title: '',
      description: '',
      category: 'Kitchen Remodeling',
      location: 'Cleveland, OH',
      year: new Date().getFullYear().toString()
    });
  };

  const deleteGalleryImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`, getAuthHeaders());
      loadData();
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  // Settings Management
  const handleUpdateSettings = async () => {
    try {
      await axios.patch(`${API}/admin/settings`, siteSettings, getAuthHeaders());
      setEditingSettings(false);
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Failed to update settings:', error);
      alert('Failed to update settings');
    }
  };

  // Gallery Image Edit
  const startEditGalleryImage = (image) => {
    setEditingGalleryImage({...image});
  };

  const updateGalleryImageField = (field, value) => {
    setEditingGalleryImage({
      ...editingGalleryImage,
      [field]: value
    });
  };

  const saveGalleryImageEdit = async () => {
    if (!editingGalleryImage) return;
    
    try {
      await axios.patch(
        `${API}/admin/gallery/${editingGalleryImage.id}`,
        {
          title: editingGalleryImage.title,
          description: editingGalleryImage.description,
          category: editingGalleryImage.category,
          location: editingGalleryImage.location,
          year: editingGalleryImage.year
        },
        getAuthHeaders()
      );
      setEditingGalleryImage(null);
      loadData();
      alert('Image updated successfully!');
    } catch (error) {
      console.error('Failed to update image:', error);
      alert('Failed to update image');
    }
  };

  // Account Management
  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    
    if (accountForm.newPassword && accountForm.newPassword !== accountForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (!accountForm.currentPassword) {
      alert('Current password is required');
      return;
    }

    try {
      const updateData = {
        current_password: accountForm.currentPassword
      };

      if (accountForm.username) {
        updateData.username = accountForm.username;
      }

      if (accountForm.newPassword) {
        updateData.new_password = accountForm.newPassword;
      }

      const response = await axios.patch(`${API}/admin/account`, updateData, getAuthHeaders());
      
      alert('Account updated successfully! Please login again.');
      localStorage.removeItem('admin_token');
      navigate('/ops/login');
    } catch (error) {
      console.error('Failed to update account:', error);
      alert(error.response?.data?.detail || 'Failed to update account');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 pt-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              <span className="text-[#D4AF37]">BuildBroz</span> Admin Dashboard
            </h1>
            <p className="text-gray-400">Owner Control Panel</p>
          </div>
          <div className="flex items-center gap-4">
            <div
              data-testid="header-total-views"
              className="hidden sm:flex flex-col items-center bg-[#1A1A1A] border border-[#D4AF37]/40 rounded-lg px-5 py-2"
            >
              <span className="text-2xl font-bold text-[#D4AF37] leading-none">
                {stats ? stats.total_views.toLocaleString() : '—'}
              </span>
              <span className="text-gray-400 text-xs mt-1">Total Views</span>
            </div>
            <Button 
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="reviews" className="w-full">
          <TabsList className="bg-[#1A1A1A] border-b border-gray-800 w-full justify-start">
            <TabsTrigger value="reviews" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Reviews
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Gallery
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Site Settings
            </TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Account
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="mt-6">
            <Card className="bg-[#1A1A1A] border-gray-800 p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Manage Reviews</h2>
              
              <div className="space-y-4">
                {reviews.length === 0 ? (
                  <p className="text-gray-400">No reviews yet.</p>
                ) : (
                  reviews.map((review) => (
                    <Card key={review.id} className="bg-[#2A2A2A] border-gray-700 p-4">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {[...Array(review.rating)].map((_, i) => (
                              <Star key={i} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />
                            ))}
                            <span className="text-gray-400 text-sm">
                              {new Date(review.date).toLocaleDateString()}
                            </span>
                          </div>
                          <h3 className="text-white font-semibold">{review.name}</h3>
                          <p className="text-[#D4AF37] text-sm">{review.project} - {review.location}</p>
                          <p className="text-gray-300 mt-2">{review.comment}</p>
                        </div>

                        <div className="flex gap-2 ml-4">
                          {review.approved ? (
                            <div className="flex items-center gap-2 text-green-500 text-sm">
                              <Check size={16} />
                              Approved
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => updateReview(review.id, { approved: true, published: true })}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check size={16} className="mr-1" />
                              Approve
                            </Button>
                          )}
                          
                          {review.published ? (
                            <Button
                              size="sm"
                              onClick={() => updateReview(review.id, { published: false })}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white"
                            >
                              <EyeOff size={16} className="mr-1" />
                              Hide
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => updateReview(review.id, { published: true })}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              <Eye size={16} className="mr-1" />
                              Publish
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            onClick={() => deleteReview(review.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>

                      <div className="flex gap-2 text-xs">
                        {review.approved && (
                          <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded">Approved</span>
                        )}
                        {review.published && (
                          <span className="bg-blue-900/30 text-blue-400 px-2 py-1 rounded">Published</span>
                        )}
                        {!review.approved && (
                          <span className="bg-red-900/30 text-red-400 px-2 py-1 rounded">Pending Approval</span>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="mt-6">
            <Card className="bg-[#1A1A1A] border-gray-800 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Manage Gallery</h2>
                <div>
                  <input
                    type="file"
                    id="gallery-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageSelect}
                    disabled={uploadingImage}
                  />
                  <Button 
                    className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" 
                    disabled={uploadingImage}
                    onClick={() => document.getElementById('gallery-upload').click()}
                  >
                    <Upload size={18} className="mr-2" />
                    {uploadingImage ? 'Uploading...' : 'Add New Image'}
                  </Button>
                </div>
              </div>

              {/* Upload Image Modal */}
              {newImageFile && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <Card className="bg-[#1A1A1A] border-gray-800 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <h3 className="text-2xl font-bold text-white mb-4">Upload New Image</h3>
                    
                    {/* Image Preview */}
                    {newImagePreview && (
                      <div className="mb-4">
                        <img 
                          src={newImagePreview} 
                          alt="Preview" 
                          className="w-full h-64 object-cover rounded-lg"
                        />
                      </div>
                    )}

                    <div className="space-y-4">
                      <div>
                        <label className="text-white mb-2 block font-medium">Title *</label>
                        <Input
                          value={newImageData.title}
                          onChange={(e) => setNewImageData({...newImageData, title: e.target.value})}
                          placeholder="e.g., Modern Kitchen Remodel"
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>

                      <div>
                        <label className="text-white mb-2 block font-medium">Description *</label>
                        <Textarea
                          value={newImageData.description}
                          onChange={(e) => setNewImageData({...newImageData, description: e.target.value})}
                          placeholder="Describe this project..."
                          rows={3}
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>

                      <div>
                        <label className="text-white mb-2 block font-medium">Category</label>
                        <select
                          value={newImageData.category}
                          onChange={(e) => setNewImageData({...newImageData, category: e.target.value})}
                          className="w-full bg-[#2A2A2A] border border-gray-700 text-white rounded-md px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                        >
                          <option value="Kitchen Remodeling">Kitchen Remodeling</option>
                          <option value="Bathroom Renovation">Bathroom Renovation</option>
                          <option value="Interior Remodeling">Interior Remodeling</option>
                          <option value="Home Addition">Home Addition</option>
                          <option value="Basement Finishing">Basement Finishing</option>
                          <option value="Roofing & Siding">Roofing & Siding</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-white mb-2 block font-medium">Location</label>
                          <Input
                            value={newImageData.location}
                            onChange={(e) => setNewImageData({...newImageData, location: e.target.value})}
                            placeholder="Cleveland, OH"
                            className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                          />
                        </div>

                        <div>
                          <label className="text-white mb-2 block font-medium">Year</label>
                          <Input
                            value={newImageData.year}
                            onChange={(e) => setNewImageData({...newImageData, year: e.target.value})}
                            placeholder="2025"
                            className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <Button
                          onClick={handleImageUpload}
                          disabled={uploadingImage}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Upload size={18} className="mr-2" />
                          {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        </Button>
                        <Button
                          onClick={cancelImageUpload}
                          disabled={uploadingImage}
                          className="bg-gray-600 hover:bg-gray-700 text-white"
                        >
                          <X size={18} className="mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {galleryImages.map((image) => (
                  <Card key={image.id} className="bg-[#2A2A2A] border-gray-700 overflow-hidden">
                    <div className="relative h-64">
                      <img
                        src={`${BACKEND_URL}${image.image_url}`}
                        alt={image.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-semibold mb-1">{image.title}</h3>
                      <p className="text-gray-400 text-sm mb-2">{image.description}</p>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-[#D4AF37] text-sm">{image.location}</span>
                        <span className="text-gray-500 text-xs">{image.year}</span>
                      </div>
                      {image.updated_at && (
                        <p className="text-gray-600 text-xs mb-3">
                          Updated: {new Date(image.updated_at).toLocaleString()}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => startEditGalleryImage(image)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Edit size={16} className="mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => deleteGalleryImage(image.id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Edit Gallery Image Modal */}
              {editingGalleryImage && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <Card className="bg-[#1A1A1A] border-gray-800 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <h3 className="text-2xl font-bold text-white mb-4">Edit Image Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-white mb-2 block font-medium">Title</label>
                        <Input
                          value={editingGalleryImage.title}
                          onChange={(e) => updateGalleryImageField('title', e.target.value)}
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>

                      <div>
                        <label className="text-white mb-2 block font-medium">Description</label>
                        <Textarea
                          value={editingGalleryImage.description}
                          onChange={(e) => updateGalleryImageField('description', e.target.value)}
                          rows={3}
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>

                      <div>
                        <label className="text-white mb-2 block font-medium">Category</label>
                        <Input
                          value={editingGalleryImage.category}
                          onChange={(e) => updateGalleryImageField('category', e.target.value)}
                          placeholder="e.g., Kitchen Remodeling"
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-white mb-2 block font-medium">Location</label>
                          <Input
                            value={editingGalleryImage.location}
                            onChange={(e) => updateGalleryImageField('location', e.target.value)}
                            className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                          />
                        </div>

                        <div>
                          <label className="text-white mb-2 block font-medium">Year</label>
                          <Input
                            value={editingGalleryImage.year}
                            onChange={(e) => updateGalleryImageField('year', e.target.value)}
                            className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <Button
                          onClick={saveGalleryImageEdit}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Save size={18} className="mr-2" />
                          Save Changes
                        </Button>
                        <Button
                          onClick={() => setEditingGalleryImage(null)}
                          className="bg-gray-600 hover:bg-gray-700 text-white"
                        >
                          <X size={18} className="mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <Card className="bg-[#1A1A1A] border-gray-800 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Site Settings</h2>
                {editingSettings ? (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleUpdateSettings}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Save size={18} className="mr-2" />
                      Save Changes
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingSettings(false);
                        loadData();
                      }}
                      className="bg-gray-600 hover:bg-gray-700 text-white"
                    >
                      <X size={18} className="mr-2" />
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setEditingSettings(true)}
                    className="bg-[#D4AF37] text-black hover:bg-[#B8941F]"
                  >
                    <Edit size={18} className="mr-2" />
                    Edit Settings
                  </Button>
                )}
              </div>

              {siteSettings && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-white mb-2 block font-medium">Phone Number 1</label>
                      <Input
                        value={siteSettings.phone1}
                        onChange={(e) => setSiteSettings({...siteSettings, phone1: e.target.value})}
                        disabled={!editingSettings}
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>

                    <div>
                      <label className="text-white mb-2 block font-medium">Phone Number 2</label>
                      <Input
                        value={siteSettings.phone2}
                        onChange={(e) => setSiteSettings({...siteSettings, phone2: e.target.value})}
                        disabled={!editingSettings}
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>

                    <div>
                      <label className="text-white mb-2 block font-medium">Email</label>
                      <Input
                        value={siteSettings.email}
                        onChange={(e) => setSiteSettings({...siteSettings, email: e.target.value})}
                        disabled={!editingSettings}
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>

                    <div>
                      <label className="text-white mb-2 block font-medium">Service Area</label>
                      <Input
                        value={siteSettings.service_area}
                        onChange={(e) => setSiteSettings({...siteSettings, service_area: e.target.value})}
                        disabled={!editingSettings}
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>

                    <div>
                      <label className="text-white mb-2 block font-medium">Company Slogan</label>
                      <Input
                        value={siteSettings.slogan}
                        onChange={(e) => setSiteSettings({...siteSettings, slogan: e.target.value})}
                        disabled={!editingSettings}
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>

                    <div>
                      <label className="text-white mb-2 block font-medium">Company Name</label>
                      <Input
                        value={siteSettings.company_name}
                        onChange={(e) => setSiteSettings({...siteSettings, company_name: e.target.value})}
                        disabled={!editingSettings}
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white mb-2 block font-medium">Home Page Hero Title</label>
                    <Input
                      value={siteSettings.home_hero_title}
                      onChange={(e) => setSiteSettings({...siteSettings, home_hero_title: e.target.value})}
                      disabled={!editingSettings}
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  <div>
                    <label className="text-white mb-2 block font-medium">Home Page Hero Subtitle</label>
                    <Input
                      value={siteSettings.home_hero_subtitle}
                      onChange={(e) => setSiteSettings({...siteSettings, home_hero_subtitle: e.target.value})}
                      disabled={!editingSettings}
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  <div>
                    <label className="text-white mb-2 block font-medium">About Page Text</label>
                    <Textarea
                      value={siteSettings.about_text}
                      onChange={(e) => setSiteSettings({...siteSettings, about_text: e.target.value})}
                      disabled={!editingSettings}
                      rows={6}
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                    />
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account" className="mt-6">
            <Card className="bg-[#1A1A1A] border-gray-800 p-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="text-[#D4AF37]" size={32} />
                <h2 className="text-2xl font-bold text-white">Account Settings</h2>
              </div>

              <form onSubmit={handleUpdateAccount} className="space-y-6 max-w-2xl">
                <div className="bg-yellow-900/20 border border-yellow-600 text-yellow-400 px-4 py-3 rounded">
                  <p className="text-sm">
                    <strong>Recovery Code:</strong> 09280928 (Store this safely - required for password recovery)
                  </p>
                </div>

                <div>
                  <label className="text-white mb-2 block font-medium">New Username (optional)</label>
                  <Input
                    value={accountForm.username}
                    onChange={(e) => setAccountForm({...accountForm, username: e.target.value})}
                    placeholder="Leave blank to keep current username"
                    className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                  />
                  <p className="text-gray-500 text-xs mt-1">Enter a new username only if you want to change it</p>
                </div>

                <div>
                  <label className="text-white mb-2 block font-medium">Current Password *</label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      value={accountForm.currentPassword}
                      onChange={(e) => setAccountForm({...accountForm, currentPassword: e.target.value})}
                      placeholder="Enter current password"
                      required
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                    >
                      {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Required to make any changes</p>
                </div>

                <div className="border-t border-gray-700 pt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Change Password (optional)</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-white mb-2 block font-medium">New Password</label>
                      <div className="relative">
                        <Input
                          type={showNewPasswordAccount ? "text" : "password"}
                          value={accountForm.newPassword}
                          onChange={(e) => setAccountForm({...accountForm, newPassword: e.target.value})}
                          placeholder="Leave blank to keep current password"
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37] pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPasswordAccount(!showNewPasswordAccount)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                        >
                          {showNewPasswordAccount ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-white mb-2 block font-medium">Confirm New Password</label>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={accountForm.confirmPassword}
                          onChange={(e) => setAccountForm({...accountForm, confirmPassword: e.target.value})}
                          placeholder="Confirm new password"
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37] pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
                >
                  <Save size={18} className="mr-2" />
                  Update Account
                </Button>

                <p className="text-gray-500 text-sm">
                  Note: You will be logged out after updating and must login again with your new credentials.
                </p>
              </form>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-6">
            <Card className="bg-[#1A1A1A] border-gray-800 p-6" data-testid="analytics-panel">
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="text-[#D4AF37]" size={32} />
                <h2 className="text-2xl font-bold text-white">Website Analytics</h2>
              </div>

              {stats ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                    <Card className="bg-[#2A2A2A] border-gray-700 p-6" data-testid="stat-total-views">
                      <div className="flex items-center gap-3 mb-2">
                        <Eye className="text-[#D4AF37]" size={24} />
                        <span className="text-gray-400">Total Views (All Time)</span>
                      </div>
                      <p className="text-4xl font-bold text-white">{stats.total_views.toLocaleString()}</p>
                    </Card>
                    <Card className="bg-[#2A2A2A] border-gray-700 p-6" data-testid="stat-today-views">
                      <div className="flex items-center gap-3 mb-2">
                        <TrendingUp className="text-[#D4AF37]" size={24} />
                        <span className="text-gray-400">Views Today</span>
                      </div>
                      <p className="text-4xl font-bold text-white">{stats.today_views.toLocaleString()}</p>
                    </Card>
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-4">Last 7 Days</h3>
                  <div className="flex items-end justify-between gap-2 h-48 mb-2" data-testid="views-chart">
                    {stats.last_7_days.map((day) => {
                      const maxCount = Math.max(...stats.last_7_days.map((d) => d.count), 1);
                      const heightPct = day.count === 0 ? 2 : Math.max((day.count / maxCount) * 100, 4);
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center justify-end h-full">
                          <span className="text-[#D4AF37] text-xs mb-1 font-semibold">{day.count}</span>
                          <div
                            className="w-full bg-gradient-to-t from-[#B8941F] to-[#D4AF37] rounded-t transition-all"
                            style={{ height: `${heightPct}%` }}
                          ></div>
                          <span className="text-gray-500 text-xs mt-2">
                            {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-gray-500 text-xs mt-4">
                    Views are counted once per visitor browser session (admin visits are excluded).
                  </p>
                </>
              ) : (
                <p className="text-gray-400">No analytics data available yet.</p>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
